import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Monitor, Laptop, Smartphone, LogOut, Trash2, ShieldCheck, MailQuestion } from "lucide-react";

/**
 * v1.2 PR-B.3 — Active Sessions card (AccountProfile).
 *
 *  - GET /auth/me/sessions
 *  - POST /auth/me/sessions/{id}/revoke
 *  - POST /auth/me/sessions/revoke-all-other
 */
function relativeTime(iso) {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    const delta = (Date.now() - dt.getTime()) / 1000;
    if (delta < 60) return `${Math.round(delta)}s ago`;
    if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
    if (delta < 86400) return `${Math.round(delta / 3600)}h ago`;
    return `${Math.round(delta / 86400)}d ago`;
  } catch {
    return iso;
  }
}

function deviceIcon(ua) {
  const lower = (ua || "").toLowerCase();
  if (lower.includes("mobile") || lower.includes("iphone") || lower.includes("android")) return Smartphone;
  return Laptop;
}

export default function SessionsCard() {
  const { user, refresh } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  // v2 PR-1 Feature 1 — new-device email toggle. Mirrors `user.new_device_email_enabled`
  // returned by `/auth/me`. Default ON (backend default) so unset → checked.
  const [alertOn, setAlertOn] = useState(user?.new_device_email_enabled !== false);
  const [savingAlert, setSavingAlert] = useState(false);

  useEffect(() => {
    setAlertOn(user?.new_device_email_enabled !== false);
  }, [user?.new_device_email_enabled]);

  const toggleAlert = async () => {
    const next = !alertOn;
    setSavingAlert(true);
    try {
      await api.patch("/auth/me/preferences", { new_device_email_enabled: next });
      setAlertOn(next);
      toast.success(next ? "New-device alerts on." : "New-device alerts off.");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingAlert(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/me/sessions");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revokeOne = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/auth/me/sessions/${id}/revoke`);
      toast.success("Session revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => {
        const { [id]: _, ...rest } = b;
        return rest;
      });
    }
  };

  const revokeAllOther = async () => {
    setBusy((b) => ({ ...b, ALL: true }));
    try {
      const { data } = await api.post("/auth/me/sessions/revoke-all-other");
      toast.success(`Signed out of ${data.revoked_count} other session(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => {
        const { ALL: _, ...rest } = b;
        return rest;
      });
    }
  };

  const otherCount = rows.filter((r) => !r.is_current).length;
  return (
    <div
      className="rounded-2xl border border-navy-100 dark:border-navy-700 bg-white dark:bg-navy-800 p-6"
      data-testid="sessions-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-navy-900 dark:text-white inline-flex items-center gap-2">
            <Monitor className="w-4 h-4 text-orange-500" /> Active sessions
          </h2>
          <p className="text-xs text-navy-500 dark:text-navy-300 mt-1">
            Every device you're currently signed in on. Sign out remotely if anything looks
            unfamiliar.
          </p>
        </div>
        {otherCount > 0 && (
          <button
            onClick={revokeAllOther}
            disabled={!!busy.ALL}
            className="text-xs px-3 py-1.5 rounded-md border border-navy-200 dark:border-navy-600 text-navy-700 dark:text-navy-100 hover:bg-navy-50 dark:hover:bg-navy-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="sessions-revoke-all-other"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out everywhere else
          </button>
        )}
      </div>

      {/* v2 PR-1 Feature 1 — new-device alert toggle. */}
      <div
        className="flex items-center justify-between gap-3 mb-4 p-3 rounded-lg border border-navy-100 dark:border-navy-700 bg-navy-50/50 dark:bg-navy-700/30"
        data-testid="new-device-alert-toggle-row"
      >
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <MailQuestion className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-navy-900 dark:text-white">
              Email me on new-device sign-ins
            </div>
            <div className="text-xs text-navy-500 dark:text-navy-300 mt-0.5">
              We&apos;ll email you when a sign-in comes from a browser, OS or network we haven&apos;t seen on this account for 30 days.
            </div>
          </div>
        </div>
        <button
          onClick={toggleAlert}
          disabled={savingAlert}
          role="switch"
          aria-checked={alertOn}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 ${
            alertOn ? "bg-orange-500" : "bg-navy-200 dark:bg-navy-600"
          }`}
          data-testid="new-device-alert-toggle"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              alertOn ? "translate-x-5" : "translate-x-0.5"
            } mt-0.5`}
          />
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-navy-500 dark:text-navy-300">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-navy-400">No active sessions.</div>
      ) : (
        <ul className="divide-y divide-navy-100 dark:divide-navy-700">
          {rows.map((s) => {
            const Icon = deviceIcon(s.user_agent);
            return (
              <li
                key={s.id}
                className="py-3 flex items-center gap-3"
                data-testid="sessions-row"
              >
                <Icon className="w-5 h-5 text-navy-400 dark:text-navy-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-navy-900 dark:text-white truncate">
                    {s.ua_summary || s.user_agent || "Unknown device"}
                    {s.is_current && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-success/10 text-success border border-success/30">
                        This device
                      </span>
                    )}
                    {s.legacy_backfill && (
                      <span
                        className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-navy-50 dark:bg-navy-700 text-navy-500 dark:text-navy-300 border border-navy-200 dark:border-navy-600"
                        title="Created automatically when v1.2 deployed — older JWT predates per-session tracking."
                      >
                        Legacy
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-navy-500 dark:text-navy-300 font-mono">
                    {s.ip || "—"} · last seen {relativeTime(s.last_seen_at)}
                  </div>
                </div>
                {s.is_current ? (
                  <button
                    disabled
                    title="Cannot revoke current session — sign out instead."
                    className="text-xs px-3 py-1.5 rounded-md text-navy-300 dark:text-navy-500 cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Current
                  </button>
                ) : (
                  <button
                    onClick={() => revokeOne(s.id)}
                    disabled={!!busy[s.id]}
                    className="text-xs px-3 py-1.5 rounded-md text-danger hover:bg-danger/5 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`sessions-revoke-${s.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {busy[s.id] ? "Revoking…" : "Revoke"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
